import { FormatListBulleted } from '@mui/icons-material';
import { Box, Button, Fab, Modal } from '@mui/material';
import Slide from '@mui/material/Slide';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as PanelCollapseIcon } from '../../../assets/panel-collapse.svg';
import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import useTripMapFilters from '../../../hooks/useTripMapFilters';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  selectCards,
  selectCurrentTrip,
  selectDays,
  selectEvents,
  selectLists,
  selectOrderedTopLevelItems,
} from '../../../redux/trip/trip.selectors';
import { addCardToList, removeCardFromList } from '../../../redux/trip/trip.slice';
import { TripDivider } from '../../../redux/trip/trip.types';
import { getOrderedLists, isPlaceAlreadyInList } from '../../../redux/trip/trip.utils';
import { selectCurrentUser } from '../../../redux/user/user.selectors';
import { Marker } from '../../../types/MapMarker';
import {
  getMarkersForCardsInListsAndExtendBounds,
  getMarkersForDaysAndExtendBounds,
} from '../../../utils/map-utils';
import { buildCardFromPlaceDetails } from '../../../utils/places-api.utils';
import MapListToast, {
  type MapListToastInfo,
} from '../../Map/components/MapListToast/MapListToast';
import GoogleMap, { type MapPoiData } from '../../Map/Map';
import TripMapFilter from './components/TripMapFilter';
import './TripMap.scss';

function formatDayLabel(dateISO: string): string {
  const [year, month, day] = dateISO.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function generateDateRange(startISO: string, endISO: string): string[] {
  const dates: string[] = [];
  const [sy, sm, sd] = startISO.split('-').map(Number);
  const [ey, em, ed] = endISO.split('-').map(Number);
  const end = new Date(ey, em - 1, ed);
  const current = new Date(sy, sm - 1, sd);
  while (current <= end) {
    dates.push(
      `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`
    );
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

interface TripMapProps {
  center?: { lat: number; lng: number };
  zoom?: number;
  markerOnClick?: (marker: Marker) => void;
  onCenterChanged?: (lat: number, lng: number) => void;
  onZoomChanged?: (zoom: number) => void;
  fitBounds: boolean;
}

/**
 * TripMap - Interactive map component for displaying trip places with filtering
 *
 * Features:
 * - Real-time Redux state synchronization across tabs
 * - Persistent filter preferences via sessionStorage
 * - Mobile-optimized filter UI with modal
 * - Current location integration
 * - Automatic bounds fitting for selected lists
 *
 * Uses custom useTripMapFilters hook for clean separation of filter state management.
 */

function TripMap({
  center,
  zoom,
  markerOnClick,
  onCenterChanged,
  onZoomChanged,
  fitBounds,
}: TripMapProps) {
  const dispatch = useAppDispatch();
  const isMobile = useMobileMediaQuery();

  // Get real-time data from Redux for immediate updates across tabs
  const cards = useAppSelector(selectCards) || {};
  const lists = useAppSelector(selectLists) || [];
  const days = useAppSelector(selectDays) || {};
  const events = useAppSelector(selectEvents) || {};
  const currentTrip = useAppSelector(selectCurrentTrip);
  const currentUser = useAppSelector(selectCurrentUser);

  // Maps googlePlaceId → list IDs that already contain that place (for checkbox pre-checking)
  const placeIdToListIds = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const list of lists) {
      for (const cardId of list.cardOrder ?? []) {
        const googlePlaceId = cards[cardId]?.googlePlaceId;
        if (googlePlaceId) {
          if (!map[googlePlaceId]) {
            map[googlePlaceId] = [];
          }
          map[googlePlaceId].push(list.id);
        }
      }
    }
    return map;
  }, [lists, cards]);

  // Maps cardId → list emoji so day markers render with the same emoji as their list
  const cardIdToListEmoji = useMemo(() => {
    const map: Record<string, string> = {};
    for (const list of lists) {
      for (const cardId of list.cardOrder ?? []) {
        map[cardId] = list.emoji;
      }
    }
    return map;
  }, [lists]);

  const orderedTopLevelItems = useAppSelector(selectOrderedTopLevelItems);

  // All lists with cards in sidebar order (top-level lists + divider-nested lists)
  const listsWithCards = useMemo(
    () =>
      currentTrip
        ? getOrderedLists(currentTrip).filter((list) => Boolean(list.cardOrder?.length))
        : [],
    [currentTrip]
  );

  // Full list of trip days when custom dates are set, each flagged with whether it has events
  const allTripDays = useMemo(() => {
    if (!currentTrip?.startDateISO || !currentTrip?.endDateISO) {
      return [];
    }
    return generateDateRange(currentTrip.startDateISO, currentTrip.endDateISO).map((dateISO) => ({
      dateISO,
      label: formatDayLabel(dateISO),
      hasEvents: Boolean(days[dateISO]?.eventOrder?.length),
    }));
  }, [currentTrip?.startDateISO, currentTrip?.endDateISO, days]);

  // Days with events — used for selection state and marker generation
  const daysWithEvents = useMemo(() => allTripDays.filter((d) => d.hasEvents), [allTripDays]);

  // Use custom hook for filter state management
  const {
    selectedLists,
    handleListToggle,
    handleSelectAll: selectAllLists,
    handleUnselectAll: unselectAllLists,
  } = useTripMapFilters({ availableLists: listsWithCards });

  const [filterOpen, setFilterOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);
  const [shouldFitBounds, setShouldFitBounds] = useState(false);
  const [toastInfo, setToastInfo] = useState<MapListToastInfo | null>(null);

  // Day filter state — initializes from sessionStorage, handlers update state directly
  const [selectedDayDateISOs, setSelectedDayDateISOs] = useState<string[]>([]);
  const daysInitializedRef = useRef(false);

  useEffect(() => {
    if (daysWithEvents.length === 0) {
      return;
    }
    const availableISOs = daysWithEvents.map((d) => d.dateISO);
    if (!daysInitializedRef.current) {
      daysInitializedRef.current = true;
      const stored = sessionStorage.getItem('selectedDays');
      if (stored) {
        const storedISOs = JSON.parse(stored) as string[];
        setSelectedDayDateISOs(availableISOs.filter((iso) => storedISOs.includes(iso)));
      } else {
        setSelectedDayDateISOs(availableISOs);
      }
    } else {
      const available = new Set(availableISOs);
      setSelectedDayDateISOs((prev) => prev.filter((iso) => available.has(iso)));
    }
  }, [daysWithEvents]);

  const handleDayToggle = useCallback((dateISO: string) => {
    setSelectedDayDateISOs((prev) => {
      const next = prev.includes(dateISO) ? prev.filter((d) => d !== dateISO) : [...prev, dateISO];
      sessionStorage.setItem('selectedDays', JSON.stringify(next));
      return next;
    });
  }, []);

  const handleSelectAllDays = useCallback(() => {
    const all = daysWithEvents.map((d) => d.dateISO);
    sessionStorage.setItem('selectedDays', JSON.stringify(all));
    setSelectedDayDateISOs(all);
  }, [daysWithEvents]);

  const handleUnselectAllDays = useCallback(() => {
    sessionStorage.setItem('selectedDays', JSON.stringify([]));
    setSelectedDayDateISOs([]);
  }, []);

  // Map ref
  const internalMapRef = useRef<google.maps.Map | null>(null);

  // Combine list markers and day markers, deduplicating by card id
  const { markers, didBoundsUpdate, bounds } = useMemo(() => {
    const b = new window.google.maps.LatLngBounds();
    const listResult = getMarkersForCardsInListsAndExtendBounds(selectedLists, cards, b);
    const dayResult = getMarkersForDaysAndExtendBounds(
      selectedDayDateISOs,
      days,
      events,
      cards,
      cardIdToListEmoji,
      b
    );
    const markerById: Record<string, Marker> = {};
    for (const m of [...listResult.markers, ...dayResult.markers]) {
      markerById[m.id] = m;
    }
    return {
      markers: Object.values(markerById),
      didBoundsUpdate: listResult.didBoundsUpdate || dayResult.didBoundsUpdate,
      bounds: b,
    };
  }, [selectedLists, selectedDayDateISOs, cards, days, events, cardIdToListEmoji]);

  // Map ref callback with bounds fitting logic
  const setMapRef = useCallback(
    (node: google.maps.Map | null) => {
      internalMapRef.current = node;

      if (node && fitBounds && didBoundsUpdate) {
        node.fitBounds(bounds);
        const zoomLevel = node.getZoom();
        if (zoomLevel && zoomLevel > 14) {
          node.setZoom(14);
        }
      }
    },
    [fitBounds, didBoundsUpdate, bounds]
  );

  const handleAddToList = useCallback(
    async (listId: string, poi: MapPoiData): Promise<void> => {
      if (!currentTrip || !currentUser) {
        return;
      }
      const targetList = lists.find((l) => l.id === listId);
      if (!targetList) {
        return;
      }
      const alreadyInList = isPlaceAlreadyInList(targetList, cards, poi.placeId);
      if (alreadyInList) {
        const cardId = (targetList.cardOrder ?? []).find(
          (id) => cards[id]?.googlePlaceId === poi.placeId
        );
        if (cardId) {
          dispatch(
            removeCardFromList({
              tripId: currentTrip.id,
              listId,
              cardId,
              cardOrder: targetList.cardOrder ?? [],
            })
          );
          setToastInfo({ placeName: poi.name, listName: targetList.name, action: 'removed' });
        }
        return;
      }
      const card = await buildCardFromPlaceDetails(poi);
      dispatch(
        addCardToList({
          tripId: currentTrip.id,
          listId,
          card: { ...card, creator: currentUser.uid },
          cardOrder: targetList.cardOrder ?? [],
        })
      );
      setToastInfo({ placeName: poi.name, listName: targetList.name, action: 'added' });
    },
    [currentTrip, currentUser, lists, cards, dispatch]
  );

  // Handle manual bounds fitting
  useEffect(() => {
    if (shouldFitBounds && internalMapRef.current) {
      const map = internalMapRef.current;
      map.fitBounds(bounds);

      const zoomLevel = map.getZoom();
      if (zoomLevel && zoomLevel > 14) {
        map.setZoom(14);
      }

      setShouldFitBounds(false);
    }
  }, [shouldFitBounds, bounds]);

  const handleSelectAll = () => {
    selectAllLists();
    setShouldFitBounds(true);
  };

  const handleUnselectAll = () => {
    unselectAllLists();
    setShouldFitBounds(false);
  };

  const handleDividerToggle = useCallback(
    (divider: TripDivider) => {
      const dividerListIds = divider.listOrder.filter((id) =>
        listsWithCards.some((l) => l.id === id)
      );
      const allSelected =
        dividerListIds.length > 0 &&
        dividerListIds.every((id) => selectedLists.some((l) => l.id === id));
      for (const id of dividerListIds) {
        const isSelected = selectedLists.some((l) => l.id === id);
        if (allSelected ? isSelected : !isSelected) {
          handleListToggle(id);
        }
      }
    },
    [listsWithCards, selectedLists, handleListToggle]
  );

  // Simplified change handler using the custom hook
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleListToggle(event.target.id);
  };

  return (
    <div className='trip-map-wrapper'>
      {!isMobile && (
        <div className={`trip-map-filter-panel${filterPanelOpen ? '' : ' closed'}`}>
          <TripMapFilter
            selectedLists={selectedLists}
            listsWithCards={listsWithCards}
            orderedTopLevelItems={orderedTopLevelItems}
            handleUnselectAll={handleUnselectAll}
            handleSelectAll={handleSelectAll}
            handleChange={handleChange}
            handleDividerToggle={handleDividerToggle}
            onClose={() => setFilterPanelOpen(false)}
            availableDays={allTripDays}
            selectedDayDateISOs={selectedDayDateISOs}
            handleDayToggle={handleDayToggle}
            handleSelectAllDays={handleSelectAllDays}
            handleUnselectAllDays={handleUnselectAllDays}
          />
        </div>
      )}

      <div className='trip-map-container'>
        <GoogleMap
          ref={setMapRef}
          center={center}
          zoom={zoom}
          markers={markers}
          markerOnClick={markerOnClick}
          onCenterChanged={onCenterChanged}
          onZoomChanged={onZoomChanged}
          showCurrentLocation={true}
          lists={lists}
          placeIdToListIds={placeIdToListIds}
          onAddToList={handleAddToList}
        />

        {!isMobile && !filterPanelOpen && (
          <Button
            className='trip-map-filter-map-toggle'
            size='small'
            endIcon={<PanelCollapseIcon />}
            onClick={() => setFilterPanelOpen(true)}
          >
            Filter
          </Button>
        )}

        {isMobile &&
          (!filterOpen ? (
            <Fab
              className='trip-map-filler-fab'
              onClick={() => setFilterOpen(true)}
            >
              <FormatListBulleted fontSize='large' />
            </Fab>
          ) : (
            <Modal
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
            >
              <Slide
                direction='up'
                in={filterOpen}
                mountOnEnter
                unmountOnExit
              >
                <Box className='trip-map-filler-container-mobile'>
                  <TripMapFilter
                    selectedLists={selectedLists}
                    listsWithCards={listsWithCards}
                    orderedTopLevelItems={orderedTopLevelItems}
                    handleUnselectAll={handleUnselectAll}
                    handleSelectAll={handleSelectAll}
                    handleChange={handleChange}
                    handleDividerToggle={handleDividerToggle}
                    availableDays={allTripDays}
                    selectedDayDateISOs={selectedDayDateISOs}
                    handleDayToggle={handleDayToggle}
                    handleSelectAllDays={handleSelectAllDays}
                    handleUnselectAllDays={handleUnselectAllDays}
                  />
                </Box>
              </Slide>
            </Modal>
          ))}

        {toastInfo && (
          <MapListToast
            open={Boolean(toastInfo)}
            placeName={toastInfo.placeName}
            listName={toastInfo.listName}
            action={toastInfo.action}
            onClose={() => setToastInfo(null)}
          />
        )}
      </div>
    </div>
  );
}

TripMap.displayName = 'TripMap';
export default TripMap;
